import React, { useCallback, useEffect, useRef, useState } from "react";
import "./LoginPage.css";
import { Button, Container } from "reactstrap";
import { useLocation, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faShieldHalved,
  faKey,
  faCaretDown,
  faEnvelope,
  faMobileScreenButton,
} from "@fortawesome/free-solid-svg-icons";
import {
  apiAuthenticate,
  apiGetAuthTypes,
  apiSendAuthCode,
} from "../../../utils/queries";
import { describeRequestError } from "../../../utils/errorMessages";
import { trackPromise } from "react-promise-tracker";
import useCountDown from "react-countdown-hook";

const channelLabel = (channel) => {
  switch (channel) {
    case "Email":
      return "Personal email";
    case "Email-Business":
      return "Work email";
    case "SMS":
      return "Personal phone";
    case "SMS-Business":
      return "Work phone";
    default:
      return channel;
  }
};

const isSmsChannel = (channel) =>
  channel === "SMS" || channel === "SMS-Business";

const channelIcon = (channel) =>
  isSmsChannel(channel) ? faMobileScreenButton : faEnvelope;

// Format milliseconds remaining as `mm:ss`. Used for the Resend-code timer.
const formatCountdown = (ms) => {
  const totalSeconds = Math.max(0, Math.ceil(ms / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
};

const SUPPORT_EMAIL = "employeeportalsupport@hafniabw.com";

export const LoginPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const codeInputRef = useRef(null);
  const [token, setToken] = useState(null);
  const [authType, setAuthType] = useState(null);
  const [selectedAuthType, setSelectedAuthType] = useState(null);
  const [authTypes, setAuthTypes] = useState(null);
  const [code, setCode] = useState("");
  const [codeErrorMessage, setCodeErrorMessage] = useState("");
  const [codeError, setCodeError] = useState(false);
  const [timeLeft, { start: _start, reset }] = useCountDown();

  const start = () => {
    const newTime = 60 * 5 * 1000;
    _start(newTime);
  };

  const restart = useCallback(() => {
    apiSendAuthCode({ channel: authType.channel.toLowerCase() }, token).then();
    setCodeError(false);
    setCodeErrorMessage("");
    start();
  }, [token, authType]);

  // Autofocus the code input as soon as the verification step renders.
  useEffect(() => {
    if (authType && codeInputRef.current) {
      codeInputRef.current.focus();
    }
  }, [authType]);

  const handleLogin = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    setCodeError(false);
    setCodeErrorMessage("");
    if (!code.trim()) {
      setCodeError(true);
      setCodeErrorMessage("Please enter the code first.");
      return;
    }
    try {
      const result = await apiAuthenticate(
        token,
        code.trim(),
        authType.channel.toLowerCase()
      );
      localStorage.setItem("authToken", result.data);
      navigate(`/${token}`);
    } catch (err) {
      setCodeError(true);
      setCodeErrorMessage(
        "That code didn't work. Please try again or request a new one."
      );
    }
  };

  const switchVerificationMethod = () => {
    setAuthType(null);
    setSelectedAuthType(null);
    setCode("");
    setCodeError(false);
    setCodeErrorMessage("");
    reset();
  };

  useEffect(() => {
    // alert(location);
    const state = location.state;
    if (state?.token === null || state?.token === undefined) {
      // navigate(`/tokenfail`);
    } else {
      setToken(state.token);
    }
  }, [location]);

  useEffect(() => {
    if (token === null) return;

    async function getAuthTypes() {
      try {
        const response = await trackPromise(apiGetAuthTypes(token));
        const data = Array.isArray(response.data) ? response.data : [response.data].filter(Boolean);
        if (data.length === 0) {
          navigate(`/tokenfail`, {
            state: {
              message: "No authentication method is configured for this form.",
              token,
              origin: "auth-types",
            },
            replace: true,
          });
        }
        if (data.length === 1) {
          await trackPromise(
            apiSendAuthCode(
              { channel: data[0].channel.toLowerCase() },
              token
            )
          );
          setAuthType(data[0]);
          start();
        } else {
          setAuthTypes(data);
        }
      } catch (e) {
        console.error("Failed to load authentication methods:", e);
        navigate(`/tokenfail`, {
          state: {
            message: describeRequestError(e),
            token,
            origin: "auth-types",
          },
          replace: true,
        });
      }
    }

    getAuthTypes().then();
  }, [token]);

  // ── Code-entry view ───────────────────────────────────────────────
  if (authType) {
    const channelMode = isSmsChannel(authType.channel) ? "sms" : "email";
    const channelDescription =
      channelMode === "sms" ? "phone number" : "email address";

    return (
      <Container className="emp-login">
        <div className="emp-login-card" role="region" aria-label="Verification">
          <div className="emp-login-card__hero">
            <div className="emp-login-card__icon-wrapper">
              <FontAwesomeIcon icon={faKey} className="emp-login-card__icon" />
            </div>
            <h1 className="emp-login-card__title">Enter your code</h1>
            <p className="emp-login-card__subtitle">
              We just sent a one-time code to your {channelDescription}.
            </p>
          </div>

          <div className="emp-login-card__body">
            <p className="emp-login-card__lead">
              Sent to{" "}
              <span className="emp-login-channel-pill" aria-label="Code destination">
                <FontAwesomeIcon icon={channelIcon(authType.channel)} />
                {authType.value}
              </span>
              . Enter the code below to continue.
            </p>

            <form onSubmit={handleLogin} noValidate>
              <div className="emp-login-card__field">
                <label className="emp-login-card__label" htmlFor="login-code">
                  One-time code
                </label>
                <input
                  ref={codeInputRef}
                  id="login-code"
                  name="code"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  className={`emp-login-code-input${codeError ? " is-invalid" : ""}`}
                  placeholder="••••••"
                  value={code}
                  onChange={(e) => {
                    setCode(e.target.value);
                    if (codeError) {
                      setCodeError(false);
                      setCodeErrorMessage("");
                    }
                  }}
                  aria-invalid={codeError ? "true" : "false"}
                />
                {codeError ? (
                  <p className="emp-login-card__error" role="alert">
                    {codeErrorMessage}
                  </p>
                ) : null}
              </div>

              <div className="emp-login-card__footer">
                <span className="emp-login-card__resend">
                  {timeLeft > 0 ? (
                    <>
                      Didn't get the code? Resend in{" "}
                      <span className="emp-login-card__resend-timer">
                        {formatCountdown(timeLeft)}
                      </span>
                    </>
                  ) : (
                    <>
                      Didn't get the code?{" "}
                      <button
                        type="button"
                        className="emp-login-card__link"
                        onClick={restart}
                      >
                        Resend code
                      </button>
                    </>
                  )}
                </span>
                <button
                  type="submit"
                  className="emp-login-card__btn"
                  disabled={!code.trim()}
                >
                  Continue
                </button>
              </div>
            </form>

            <hr className="emp-login-card__divider" />
            <p className="emp-login-card__support">
              {authTypes && authTypes.length > 1 ? (
                <>
                  <button
                    type="button"
                    className="emp-login-card__link"
                    onClick={switchVerificationMethod}
                  >
                    Use a different method
                  </button>
                  <br />
                </>
              ) : null}
              Trouble signing in? Contact{" "}
              <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
            </p>
          </div>
        </div>
      </Container>
    );
  }

  // ── Method-picker view ────────────────────────────────────────────
  if (authTypes && authTypes.length > 0) {
    return (
      <Container className="emp-login">
        <div className="emp-login-card" role="region" aria-label="Verify it's you">
          <div className="emp-login-card__hero">
            <div className="emp-login-card__icon-wrapper">
              <FontAwesomeIcon icon={faShieldHalved} className="emp-login-card__icon" />
            </div>
            <h1 className="emp-login-card__title">Verify it's you</h1>
            <p className="emp-login-card__subtitle">
              Choose where you'd like to receive your one-time code.
            </p>
          </div>

          <div className="emp-login-card__body">
            <p className="emp-login-card__lead">
              We'll send a 6-digit code to confirm this is you. The code is
              valid for a few minutes.
            </p>

            <div className="emp-login-card__field">
              <label className="emp-login-card__label" htmlFor="login-channel">
                Send code to
              </label>
              <div className="emp-login-card__select-wrap">
                <select
                  id="login-channel"
                  name="auth-type"
                  className="emp-login-card__select"
                  onChange={(event) => {
                    if (event.target.value === "null") {
                      setSelectedAuthType(null);
                      return;
                    }
                    setSelectedAuthType(JSON.parse(event.target.value));
                  }}
                >
                  <option value="null">Select…</option>
                  {authTypes.map((type) => (
                    <option
                      key={`${type.channel}-${type.value}`}
                      value={JSON.stringify(type)}
                    >
                      {channelLabel(type.channel)} — {type.value}
                    </option>
                  ))}
                </select>
                <span className="emp-login-card__select-caret" aria-hidden="true">
                  <FontAwesomeIcon icon={faCaretDown} />
                </span>
              </div>
            </div>

            <div className="emp-login-card__footer">
              <span className="emp-login-card__resend" />
              <button
                type="button"
                className="emp-login-card__btn"
                disabled={selectedAuthType === null}
                onClick={async () => {
                  if (selectedAuthType === null) return;
                  await trackPromise(
                    apiSendAuthCode(
                      { channel: selectedAuthType.channel.toLowerCase() },
                      token
                    )
                  );
                  setAuthType(selectedAuthType);
                  start();
                }}
              >
                Send code
              </button>
            </div>

            <hr className="emp-login-card__divider" />
            <p className="emp-login-card__support">
              Trouble signing in? Contact{" "}
              <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
            </p>
          </div>
        </div>
      </Container>
    );
  }

  // ── Loading view (auth types not loaded yet) ─────────────────────
  return (
    <Container className="emp-login">
      <div className="emp-login-card" role="status" aria-label="Loading">
        <div className="emp-login-card__loading">Loading…</div>
      </div>
    </Container>
  );
};
